import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientMembership, MembershipStatus, SellMembership } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-13 Feature 3b — RECURRING MEMBERSHIPS (LRU-14, the other half of packages). A `£X/month` subscription
 * on the SALON's CONNECTED Stripe account, sold against a `membership_recurring` catalogue package
 * (data/packages.ts), built behind an INERT gateway so it ships gate-green WITHOUT Stripe keys (the same
 * posture as WS-8 deposits / WS-10 SaaS billing). TanStack Query over the generated client, mirroring
 * data/packages.ts (per-client list + idempotent money-adjacent mutations) and data/billing.ts (the
 * one-click end-of-period cancel).
 *
 * HONESTY (non-negotiable, ADR-0009): the membership's status / next renewal (`currentPeriodEnd`) /
 * `cancelAtPeriodEnd` / member benefit (`benefitDiscountBps`) are a RECONCILED CACHE of the
 * connected-account subscription — SERVER figures the UI only DISPLAYS. The console NEVER computes a charge,
 * a renewal date, a benefit reduction, or any money in JS. Under the fake gateway (staging) the subscription
 * is SYNTHETIC and INERT — `stripeSubscriptionId` / `stripeCustomerId` are NULL and NO real charge is taken;
 * the UI surfaces "membership active" WITHOUT implying money moved.
 *
 * The recurring kind is a SEPARATE plane from the prepaid sell: `POST /v1/client-packages` (the prepaid
 * sell) REJECTS `kind='membership_recurring'` with 422, so a membership SKU must be routed to
 * `POST /v1/client-memberships` instead. SELL is IDEMPOTENT on a client-supplied `idempotencyKey` (the
 * caller mints it once per intent and gates the button on `isPending`, so a double-tap is a safe replay that
 * creates no second subscription). CANCEL is one click, effective END OF PERIOD (the brand rule — no
 * retention friction); it takes only the path id (no body). 422/409 surface faithfully via ApiClientError.
 */

// ── Reads (money-adjacent; LIVE — staleTime 0, never persisted) ──────────────────────

/**
 * A client's recurring memberships with the SERVER's reconciled subscription state (status, next renewal,
 * cancel-scheduled flag, frozen member benefit). Gated on a clientId so a walk-in / closed strip doesn't
 * fire. Reads live (staleTime 0): a subscription status / renewal date is the source of truth the till and
 * the client record re-read. Optionally status-filtered.
 */
export function useClientMemberships(clientId: string | null, status?: MembershipStatus) {
  return useQuery({
    queryKey: qk.clientMemberships(clientId ?? null, status ?? null),
    enabled: Boolean(clientId),
    staleTime: 0,
    queryFn: async (): Promise<ClientMembership[]> =>
      unwrap(
        await api.GET('/v1/client-memberships', {
          params: { query: { clientId: clientId!, status } },
        }),
      ),
  });
}

/**
 * One recurring membership by id with its reconciled subscription state (the staff detail view). Gated on
 * an id so a closed modal doesn't fire. Reads live (staleTime 0): the status/renewal is a money-adjacent
 * server figure.
 */
export function useClientMembership(id: string | null) {
  return useQuery({
    queryKey: id ? qk.clientMembership(id) : ['client-membership', 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<ClientMembership> =>
      unwrap(await api.GET('/v1/client-memberships/{id}', { params: { path: { id: id! } } })),
  });
}

// ── Mutations (all idempotent / single-confirm; gate the button on `isPending`) ──────

/**
 * SELL a recurring membership to a client — create the subscription on the salon's CONNECTED account via
 * the gateway (FAKE → synthetic, INERT on staging: NO charge), freeze the member benefit from the package,
 * insert the client_membership. The `packageId` MUST be a `membership_recurring` catalogue SKU — the
 * separate prepaid sell (`POST /v1/client-packages`) 422s this kind, so the modal routes a membership SKU
 * here instead. Idempotent on `idempotencyKey` minted once per intent; the button is gated on `isPending`
 * so a double-tap can't create a second subscription, and a 422/409 surfaces faithfully.
 */
export function useSellMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SellMembership): Promise<ClientMembership> =>
      unwrap(await api.POST('/v1/client-memberships', { body: body as never })),
    onSuccess: (cm) => invalidateMemberships(qc, cm.clientId),
  });
}

/**
 * CANCEL a membership at the END OF THE PAID PERIOD — one click, single confirm, NO retention flow (the
 * brand rule; mirrors the WS-10 one-click SaaS cancel). The route takes only the path id (no body): the
 * server schedules `cancelAtPeriodEnd` and returns the now-scheduled membership; the client keeps the
 * benefit until the period end. No money is computed here — the server figures the effective date.
 */
export function useCancelMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ClientMembership> =>
      unwrap(await api.POST('/v1/client-memberships/{id}/cancel', { params: { path: { id } } })),
    onSuccess: (cm) => {
      qc.setQueryData(qk.clientMembership(cm.id), cm);
      invalidateMemberships(qc, cm.clientId);
    },
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/**
 * A sell/cancel shifts the client's memberships list (status / cancel-scheduled / renewal). The benefit is
 * a till `discount` line applied LIVE off the membership state, so a new/cancelled membership can change a
 * later checkout — but the till reads the benefit fresh per basket, so there is no separate cached total to
 * re-base here; we re-read the membership list (the `['client-memberships']` prefix covers every keyed
 * client/tab in the registry).
 */
function invalidateMemberships(qc: ReturnType<typeof useQueryClient>, _clientId: string): void {
  void qc.invalidateQueries({ queryKey: ['client-memberships'] });
}
